import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { exportOT36Excel, exportOT36Pdf } from "../../shared/dashboard/ot36Export";

const OT36Dialog = ({ fetchOT36, month, year, companyName }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [weeks, setWeeks] = useState([]);
  const [exporting, setExporting] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const data = await fetchOT36();
      setWeeks(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("OT36 fetch failed:", e);
      setWeeks([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPeople = weeks.reduce((a, w) => a + (w.employees ? w.employees.length : 0), 0);
  const hasData = totalPeople > 0;

  const handleExport = async (kind) => {
    if (!hasData || exporting) return;
    setExporting(true);
    try {
      const meta = { month, year, companyName };
      if (kind === "excel") await exportOT36Excel(weeks, meta);
      else await exportOT36Pdf(weeks, meta);
    } catch (e) {
      console.error("OT36 export failed:", e);
    } finally {
      setExporting(false);
    }
  };

  const exportBtnSx = {
    textTransform: "none",
    minWidth: 0,
    padding: "3px 10px",
    fontSize: 13,
    borderRadius: "8px",
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        disabled={loading}
        startIcon={
          loading ? (
            <CircularProgress size={13} />
          ) : (
            <Icon className="fal fa-list" style={{ fontSize: 13 }} />
          )
        }
        onClick={handleOpen}
        sx={{
          textTransform: "none",
          color: "#007afe",
          padding: "2px 8px",
          minWidth: 0,
          fontSize: 13,
          whiteSpace: "nowrap",
          "&:hover": { backgroundColor: "rgba(0,122,254,0.06)" },
        }}
      >
        {loading ? "กำลังโหลด..." : "ดูรายชื่อ"}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: "8px" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
              พนักงานทำงานล่วงเวลาเกิน 36 ชม.
            </Typography>
            {!loading && (
              <Typography sx={{ fontSize: 13, color: "#919eab" }}>
                รวม {totalPeople} รายการ (นับซ้ำได้ถ้าเกินหลายสัปดาห์)
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Button
              size="small"
              variant="outlined"
              color="success"
              disabled={!hasData || loading || exporting}
              onClick={() => handleExport("excel")}
              startIcon={<Icon className="fal fa-file-excel" style={{ fontSize: 14 }} />}
              sx={exportBtnSx}
            >
              Excel
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!hasData || loading || exporting}
              onClick={() => handleExport("pdf")}
              startIcon={<Icon className="fal fa-file-pdf" style={{ fontSize: 14 }} />}
              sx={exportBtnSx}
            >
              PDF
            </Button>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "65vh" }}>
          {loading ? (
            <Box sx={{ textAlign: "center", padding: "32px" }}>
              <CircularProgress size={28} />
              <Typography sx={{ marginTop: "12px", color: "#919eab" }}>กำลังโหลด...</Typography>
            </Box>
          ) : weeks.every((w) => !w.employees || w.employees.length === 0) ? (
            <Typography sx={{ textAlign: "center", padding: "32px", color: "#919eab" }}>
              ไม่มีพนักงานทำงานล่วงเวลาเกิน 36 ชม.
            </Typography>
          ) : (
            weeks.map((w, wi) =>
              w.employees && w.employees.length > 0 ? (
                <Box key={wi} sx={{ marginBottom: "20px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    <Icon className="fal fa-calendar-week" style={{ color: "#007afe" }} />
                    <Typography sx={{ fontWeight: 700 }}>
                      {w.label} ({w.employees.length} คน)
                    </Typography>
                  </Box>
                  <Divider sx={{ marginBottom: "8px" }} />
                  {w.employees.map((e, ei) => (
                    <Box
                      key={ei}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "6px 4px",
                        borderBottom: "1px solid #f5f5f5",
                      }}
                    >
                      <Avatar src={e.imageProfile || e.imageName} sx={{ width: 30, height: 30, marginRight: "10px" }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography sx={{ fontSize: 14 }}>
                          {e.firstname} {e.lastname}
                        </Typography>
                        {(e.companyName || e.sectionName) && (
                          <Typography sx={{ fontSize: 12, color: "#919eab" }}>
                            {e.companyName || e.sectionName}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontWeight: 700, color: "#e53935" }}>
                        {e.amountHours} ชม.
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : null
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OT36Dialog;
